import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';
import { setVizPath } from '~src/app/actions/spaces/SetVizPathAction';
import { VizPathInitialState } from '~src/app/reducers/VizPathReducer';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { selectVizPath } from '~src/app/selectors/VizPathSelector';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { BrowserTitleService } from '~src/commonInfra/services/BrowserTitleService';
import { cssClass } from '~src/style';
import {
    fillViewPort, infoBarContentStyle, infoBarStyle, vizClass, vizStyle, vizWithInfoBarStyle
} from '~src/style/VizViewerStyles';
import { IViz, VizType } from '~src/ts/models/Viz';

import { ButtonWidget } from '@tableau/widgets';

import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { VizProps, VizView } from '../VizSpace/Viz';

export interface RevisionHistoryInfo {
  readonly workbookUrlId: string;
  readonly revisionNumber: string;  // should be number
  readonly sheetUrlId: string;
}

export interface RevisionHistorySpaceProviderOwnProps {
  readonly revisionHistoryInfo: RevisionHistoryInfo;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
}

interface RevisionHistorySpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
}

interface RevisionHistorySpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
}

const stylePrefix = 'tb-revision-history';
const buttonStyle = cssClass(stylePrefix, 'cancel-button', {
  position: 'absolute',
  top: '12px',
  right: '18px'
});
const headerStyle = cssClass(stylePrefix, 'title', {
  fontWeight: 'bold',
  fontSize: '18px',
  paddingLeft: '18px',
  marginBlockEnd: '0px',
  marginBlockStart: '0px'
});


export type RevisionHistorySpaceProviderProps = RevisionHistorySpaceProviderOwnProps & RevisionHistorySpaceProviderStateProps & RevisionHistorySpaceProviderDispatchProps;

export class RevisionHistorySpaceProvider extends React.Component<RevisionHistorySpaceProviderProps> {

  private vizProps: VizProps = {
    viz: null,
    dalert: null,
    onVizPathChange: () => {},
    onVizInstanceIdChange: () => {},
    onVizIsExplicitOriginalVizChange: () => {},
    clearDataAlertRoute: () => {}
  };
  private vizContainer: HTMLElement = null;
  private viz: tb.IViz = null;

  public constructor(props: RevisionHistorySpaceProviderProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public render() {
    return (
      <>
        <div className={infoBarStyle} data-tb-test-id="revision-history-info-bar">
          <div className={infoBarContentStyle}>
            <h1 className={headerStyle} data-tb-test-id="revision-history-header">
              { Messages.revisionHistoryPreviewTitle({ REVISION_NUMBER: this.props.revisionHistoryInfo.revisionNumber }) }
            </h1>
            <ButtonWidget
              className={buttonStyle}
              onClick={() => window.close()}
              testId="revision-viz-preview-close">
              {Messages.revisionHistoryPreviewCloseButtonLabel()}
            </ButtonWidget>
          </div>
        </div>
        <div style={vizWithInfoBarStyle}>
          <div style={fillViewPort}>
            <div ref={c => this.vizContainer = c} style={vizStyle} className={vizClass} id="viz" data-tb-test-id="revision-viz"/>
          </div>
        </div>
      </>
    );
  }

  public componentDidMount() {
    // initial viz path for analysis vizzes
    this.props.setVizPath(getRevisionHistoryVizPath(this.props.revisionHistoryInfo));
    BrowserTitleService.setTitleToServerName();

    const path = getRevisionHistoryVizPath(this.props.revisionHistoryInfo);

    this.vizProps.viz = {
      path: path,
      vizType: VizType.RevisionPreview,
      siteUrlName: this.props.sessionSiteUrlName,
      params: queryString.parse(this.props.routeLocationSearch || ''),
    };

    this.loadViz();
  }

  public componentDidUpdate(prevProps: RevisionHistorySpaceProviderProps) {
    const prevVizPathFromUrl = getRevisionHistoryVizPath(prevProps.revisionHistoryInfo);
    const currVizPathFromUrl = getRevisionHistoryVizPath(this.props.revisionHistoryInfo);
    const vizPathFromAppState = this.props.vizPath;

    if (prevVizPathFromUrl !== currVizPathFromUrl && currVizPathFromUrl !== vizPathFromAppState) {
      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      this.props.setVizPath(currVizPathFromUrl);

      if (this.vizProps) {
        const previousViz: IViz = this.vizProps.viz;

        this.vizProps.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.RevisionPreview,
          params: queryString.parse(this.props.routeLocationSearch || ''),
          isExplicitOriginalViz: previousViz.isExplicitOriginalViz,
          instanceId: previousViz.instanceId,
        };
        this.loadViz();
      }
    }
  }

  public shouldComponentUpdate(nextProps: RevisionHistorySpaceProviderProps) {
    const nextVizPathFromUrl = getRevisionHistoryVizPath(nextProps.revisionHistoryInfo);
    if (nextProps.vizPath !== VizPathInitialState &&
      nextProps.vizPath !== this.props.vizPath &&
      nextProps.vizPath !== nextVizPathFromUrl) {
      // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
      // so don't rerender
      return false;
    }

    return true;
  }

  public componentWillUnmount() {
    this.viz && this.viz.dispose();
  }

  private loadViz() {
    this.viz && this.viz.dispose();
    this.viz = VizView.loadViz(this.vizProps, this.vizContainer);
  }
}

export function getRevisionHistoryVizPath(revisionHistoryInfo: RevisionHistoryInfo) {
  // use compact because sheetUrlId can be undefined
  const pathElements = _.compact([
    revisionHistoryInfo.workbookUrlId,
    revisionHistoryInfo.sheetUrlId,
    'history',
    revisionHistoryInfo.revisionNumber
  ]);
  const path = pathElements.join('/');
  return path;
}

const mapStateToProps: MapStateToProps<RevisionHistorySpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState)
});

const mapDispatchToProps: MapDispatchToProps<RevisionHistorySpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath
}, dispatch);

export const ConnectedRevisionHistorySpaceProvider: React.ComponentClass<RevisionHistorySpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(RevisionHistorySpaceProvider);
